/**
 * apiErrorMessage.js — say what actually went wrong.
 *
 * ★★★ WHY THIS EXISTS. A TestFlight report on Build 47 said only "Does not work"
 *     over a screen showing "Failed to initialize setup". That string could not be
 *     traced to a cause from source, because the screen produced it like this:
 *
 *         setError(err.error || t('errors2.failedToInitSetup'))
 *
 *     `err.error` exists ONLY when the server answered a JSON body carrying that
 *     key. apiFetch throws three other shapes — a 5xx behind an HTML error page
 *     becomes `{status, message}`, a timeout becomes `{status: 408, message}`, a
 *     dropped connection becomes `{status: 0, message}` — and every one of them
 *     fell through to the same four words. A 403 with a real explanation, a 429
 *     telling the patient to wait, and a network drop were indistinguishable on
 *     screen AND in the report.
 *
 * ★★★ THE STATUS CODE IS PART OF THE MESSAGE, DELIBERATELY. It is the one token
 *     that survives into a screenshot, and a screenshot is what we get.
 *
 * Pure: no i18n, no fetching. The caller passes its own translated fallback.
 */

/** The server's own words if it gave any, else the transport's, else null. */
export function apiErrorDetail(err) {
  if (!err) return null
  if (typeof err === 'string') return err.trim() || null
  const detail = err.error || err.detail || err.message
  if (typeof detail !== 'string') return null
  const trimmed = detail.trim()
  return trimmed || null
}

/** `" (429)"` — or `''` when there is no useful code. Status 0 is a network drop. */
export function apiErrorCode(err) {
  const status = err && err.status
  if (typeof status !== 'number' || status === 0) return ''
  return ` (${status})`
}

/**
 * What to put on screen.
 * @param {any} err     the value apiFetch threw
 * @param {string} fallback  an ALREADY TRANSLATED fallback for the detail-less case
 */
export function apiErrorMessage(err, fallback) {
  const detail = apiErrorDetail(err)
  return `${detail || fallback || ''}${apiErrorCode(err)}`.trim()
}
